/**
 * P3-36: SpaceBuildingBtn —— Space tab 通用建筑/任务按钮。
 *
 * 复用 Bonfire BuildingBtn 的 snapshot 逻辑与 LinkListDropdown，但 controller
 * 与 opts 由 Space tab 传入（SpaceProgramBtnController /
 * PlanetBuildingBtnController）。
 *
 * 对齐旧 BuildingStackableBtn + BuildingStackableBtnController 行为：
 * - 名称/价格/禁用/limited 由 fetchModel 直传；
 * - sell/togglable/on-off/auto/staging links 与 BuildingBtn 一致；
 * - 点击 buyItem + game.render()；
 * - tooltip 走 ButtonModernHelper.getTooltipHTML。
 */
import { useCallback, useMemo, type MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { GameButton, GameButtonLink } from "../../components/GameButton";
import type { GameButtonLinkModel } from "../../components/GameButton";
import {
  LinkListDropdown,
  toBuildingSnapshot,
  type BuildingBtnControllerLike,
  type LinkListItem,
  type ModelLinkShape,
} from "../bonfire/BuildingBtn";
import {
  makeModernTooltip,
  shallowSnapEq,
  useButtonModel,
} from "../bonfire/useButtonModel";
import type { ModernButtonController } from "../bonfire/useButtonModel";

export interface SpaceBuildingBtnProps {
  /** 程序/建筑 id（对应 model.options.id） */
  id: string;
  /** 显示名称（model.options.name） */
  name: string;
  /** 描述（model.options.description） */
  description: string;
  /** controller 构造器访问函数 */
  getController: () => (new (game: GamePage) => BuildingBtnControllerLike) | undefined;
  /** 传给 controller.fetchModel 的 opts.type */
  type: string;
  /** twoRow 布局 */
  twoRow?: boolean;
}

export function SpaceBuildingBtn({
  id,
  name,
  description,
  getController,
  type,
  twoRow = false,
}: SpaceBuildingBtnProps) {
  const controller = useMemo(() => {
    const game = getGame();
    const Ctor = getController();
    if (!game || !Ctor) {
      return null;
    }
    return new Ctor(game) as BuildingBtnControllerLike;
  }, [getController]);

  const createOpts = useCallback(
    () => ({
      id,
      name,
      description,
      type,
      twoRow,
    }),
    [id, name, description, type, twoRow],
  );

  const { snap, modelRef } = useButtonModel(
    controller as ModernButtonController,
    createOpts,
    toBuildingSnapshot,
    shallowSnapEq,
  );

  const onClick = useCallback(
    (event: MouseEvent) => {
      controller?.buyItem?.(modelRef.current, event);
      const game = getGame() as (GamePage & { render?: () => void }) | null;
      game?.render?.();
    },
    [controller, modelRef],
  );

  const getTooltip = useMemo(
    () => makeModernTooltip(() => controller as ModernButtonController | null, createOpts),
    [controller, createOpts],
  );

  const getSelectedObject = useCallback(() => modelRef.current, [modelRef]);

  if (!snap) {
    return null;
  }

  const domClassName =
    (snap.hasSellLink ? " hasSellLink" : "") +
    (snap.smallText ? " small-text" : "") +
    (snap.bldStateClass ? " " + snap.bldStateClass : "");
  const domStyle = snap.fontSize90 ? { fontSize: "90%" } : undefined;

  const model = () => modelRef.current;
  const parts: React.ReactNode[] = [];

  if (snap.hasSellLink) {
    parts.push(
      <GameButtonLink
        key="sell"
        link={{
          title: t("btn.sell.minor"),
          visible: snap.sellShown,
          handler: (event) => controller?.sell(event, model()),
        }}
      />,
    );
  }

  if (snap.togglable) {
    parts.push(
      <LinkListDropdown
        key="remove"
        items={[
          { id: "off1", title: "-", handler: () => controller?.off(model()) },
          { id: "off25", title: "-25", handler: () => controller?.off(model(), 25) },
          {
            id: "offAll",
            title: "-" + t("btn.all.minor"),
            handler: () => controller?.offAll(model()),
          },
        ] as LinkListItem[]}
      />,
      <LinkListDropdown
        key="add"
        rootEnabled={snap.addEnabled}
        items={[
          { id: "add1", title: "+", handler: () => controller?.on(model()) },
          { id: "add25", title: "+25", handler: () => controller?.on(model(), 25) },
          {
            id: "add",
            title: "+" + t("btn.all.minor"),
            handler: () => controller?.onAll(model()),
          },
        ] as LinkListItem[]}
      />,
    );
  }

  if (snap.onOffTitle !== null) {
    const link: GameButtonLinkModel = {
      title: snap.onOffTitle,
      cssClass: snap.onOffCssClass ?? undefined,
      tooltip: snap.onOffTooltip ?? undefined,
      handler: () => {
        const m = model() as { togglableOnOffLink?: ModelLinkShape } | null;
        m?.togglableOnOffLink?.handler?.();
      },
    };
    parts.push(<GameButtonLink key="onoff" link={link} />);
  }

  if (snap.autoTitle !== null) {
    const link: GameButtonLinkModel = {
      title: snap.autoTitle,
      cssClass: snap.autoCssClass ?? undefined,
      tooltip: snap.autoTooltip ?? undefined,
      handler: () => {
        const m = model() as { toggleAutomationLink?: ModelLinkShape } | null;
        m?.toggleAutomationLink?.handler?.();
      },
    };
    parts.push(<GameButtonLink key="auto" link={link} />);
  }

  if (snap.stageKey) {
    snap.stageKey.split("").forEach((title, i) => {
      parts.push(
        <GameButtonLink
          key={"stage" + i}
          link={{
            title,
            handler: () => {
              const m = model() as { stageLinks?: ModelLinkShape[] } | null;
              m?.stageLinks?.[i]?.handler?.();
            },
          }}
        />,
      );
    });
  }

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      visible={snap.visible}
      enabled={snap.enabled}
      resourceIsLimited={snap.resourceIsLimited}
      twoRow={snap.twoRow}
      domClassName={domClassName}
      domStyle={domStyle}
      getTooltip={getTooltip}
      onClick={onClick}
      getSelectedObject={getSelectedObject}
    >
      {parts}
    </GameButton>
  );
}
